import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export type MeetingAttendee = {
  user: string;
  userType: string;
};

export type Meeting = {
  _id: string;
  title: string;
  description?: string;
  attendees: MeetingAttendee[];
  scheduledDate: string;
  duration: number;
  meetingPlatform?: string;
  projectId?: string;
  agenda?: string;
  meetingLink?: string;
  isRecurring?: boolean;
  createdBy?: string;
  org?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type MeetingsPayload = {
  title: string;
  description?: string;
  attendees: MeetingAttendee[];
  scheduledDate: string;
  duration: number;
  meetingPlatform?: string;
  projectId?: string;
  agenda?: string;
  meetingLink?: string;
  isRecurring?: boolean;
};

export type MeetingsResponse = {
  success: boolean;
  message: string;
  data: Meeting;
};

const addMeeting = async (
  payload: MeetingsPayload
): Promise<MeetingsResponse> => {
  const { data } = await customAxios.post<MeetingsResponse>(
    "/meetings",
    payload
  );
  return data;
};

export const useAddMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation<MeetingsResponse, AxiosError, MeetingsPayload>({
    mutationFn: addMeeting,
    onSuccess: async (data) => {
      showNotification({
        title: "Success",
        message: "Added meeting successfully",
        color: "green",
      });

      await queryClient.invalidateQueries(["meetings"]);
    },
    onError: (error) =>
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to add meeting",
        color: "red",
      }),
  });
};
